import { javascript } from "@codemirror/lang-javascript";
import { json } from "@codemirror/lang-json";
import { markdown } from "@codemirror/lang-markdown";
import { python } from "@codemirror/lang-python";
import { StreamLanguage } from "@codemirror/language";
import { dockerFile } from "@codemirror/legacy-modes/mode/dockerfile";
import { mbox } from "@codemirror/legacy-modes/mode/mbox";
import { shell } from "@codemirror/legacy-modes/mode/shell";
import type { EditorState } from "@codemirror/state";
import { StateField, type Extension } from "@codemirror/state";
import { Decoration, EditorView, type DecorationSet } from "@codemirror/view";
import { tags } from "@lezer/highlight";

// Remap mbox's token names onto the tags our highlight theme already styles.
const emlLanguage = StreamLanguage.define({
  ...mbox,
  tokenTable: {
    atom: tags.keyword,
    header: tags.string,
    link: tags.string,
  },
});

function frontmatterDecorations(state: EditorState): DecorationSet {
  const { doc } = state;
  if (doc.line(1).text !== "---") return Decoration.none;
  for (let lineNumber = 2; lineNumber <= doc.lines; lineNumber++) {
    const line = doc.line(lineNumber);
    if (line.text !== "---") continue;
    return Decoration.set([
      Decoration.mark({ class: "cm-frontmatter" }).range(0, line.to),
    ]);
  }
  return Decoration.none;
}

const frontmatterField = StateField.define<DecorationSet>({
  create: frontmatterDecorations,
  update: (decorations, transaction) =>
    transaction.docChanged
      ? frontmatterDecorations(transaction.state)
      : decorations,
  provide: (field) => EditorView.decorations.from(field),
});

const frontmatterTheme = EditorView.baseTheme({
  ".cm-frontmatter": {
    color: "var(--text-secondary) !important",
  },
  ".cm-frontmatter *": {
    color: "var(--text-secondary) !important",
  },
});

export function getLanguageExtension(filePath: string): Extension | null {
  if (filePath.endsWith(".py")) return python();
  if (filePath.endsWith(".tsx"))
    return javascript({ jsx: true, typescript: true });
  if (filePath.endsWith(".ts")) return javascript({ typescript: true });
  if (filePath.endsWith(".json")) return json();
  if (filePath.endsWith(".md") || filePath.endsWith(".markdown")) {
    return [markdown(), frontmatterField, frontmatterTheme];
  }
  if (filePath.endsWith(".sh")) return StreamLanguage.define(shell);
  if (filePath.endsWith(".eml")) return emlLanguage;
  if (filePath.endsWith("Dockerfile")) return StreamLanguage.define(dockerFile);
  return null;
}
